import * as React from 'react';
import { distanceToKm, formatDuration } from '../lib/format';
import { routeColor } from '../lib/colors';

interface Props {
  response: any;
  elapsedMs: number;
  selectedIdx: number;
  visibleRoutes: boolean[];
  onSelect: (i: number) => void;
  onToggleVisible: (i: number) => void;
}

interface RouteStats {
  km: string;
  dur: string;
  avgSpeed: number | null;
  detourFactor: number | null;
  turnCount: number;
  keeps: number;
  roundabouts: number;
  uturns: number;
  ascent: number | null;
  descent: number | null;
  tollKm: string | null;
}

// ORS instruction types:
//   0-5 turns (Left/Right/Sharp/Slight), 6 continue, 7/8 roundabout enter/exit,
//   9 U-turn, 10 finish, 11 depart, 12/13 keep left/right.
function computeStats(route: any, units: string): RouteStats {
  const s = route.summary || {};
  const km = distanceToKm(s.distance, units).toFixed(2);
  const dur = formatDuration(s.duration);
  const toll = route.extras?.tollways?.summary?.find((x: any) => x.value === 1);
  const tollKm = toll ? distanceToKm(toll.distance, units).toFixed(1) : null;

  let avgSpeed: number | null = null;
  let detourFactor: number | null = null;
  let turnCount = 0, roundabouts = 0, keeps = 0, uturns = 0;

  if (route._stats) {
    avgSpeed = route._stats.avgSpeed;
    detourFactor = route._stats.detourFactor;
    turnCount = route._stats.turnCount || 0;
    roundabouts = route._stats.roundabouts || 0;
    keeps = route._stats.keeps || 0;
    uturns = route._stats.uturns || 0;
  } else {
    const segments = route.segments || [];
    let totalAvg: number | null = null, segCount = 0, totalDet: number | null = null;
    let stepCount = 0, stepAvg: number | null = null, stepDet: number | null = null;
    for (const seg of segments) {
      segCount++;
      if (seg.avgspeed != null) totalAvg = (totalAvg || 0) + seg.avgspeed;
      if (seg.detourfactor != null) totalDet = (totalDet || 0) + seg.detourfactor;
      for (const step of (seg.steps || [])) {
        stepCount++;
        const t = step.type;
        if (t >= 0 && t <= 5) turnCount++;
        else if (t === 7) roundabouts++;
        else if (t === 9) uturns++;
        else if (t === 12 || t === 13) keeps++;
        if (step.avgspeed != null) stepAvg = (stepAvg || 0) + step.avgspeed;
        if (step.detourfactor != null) stepDet = (stepDet || 0) + step.detourfactor;
      }
    }
    avgSpeed = totalAvg != null && segCount ? +(totalAvg / segCount).toFixed(1)
      : stepAvg != null && stepCount ? +(stepAvg / stepCount).toFixed(1) : null;
    detourFactor = totalDet != null && segCount ? +(totalDet / segCount).toFixed(2)
      : stepDet != null && stepCount ? +(stepDet / stepCount).toFixed(2) : null;
  }

  return {
    km, dur, avgSpeed, detourFactor,
    turnCount, keeps, roundabouts, uturns,
    ascent: s.ascent ?? null,
    descent: s.descent ?? null,
    tollKm,
  };
}

export default function RoutesPanel({
  response, elapsedMs, selectedIdx, visibleRoutes, onSelect, onToggleVisible,
}: Props) {
  if (!response?.routes?.length) return null;
  const units = response?.metadata?.query?.units || 'm';
  const routes = response.routes as any[];

  // Compute all stats once so we can also derive deltas from the primary.
  const stats = routes.map(r => computeStats(r, units));
  const primaryKm = parseFloat(stats[0].km);
  const primaryDur = routes[0].summary?.duration ?? 0;

  // Detect duplicate routes (ORS sometimes returns identical alternates when it
  // can't find enough distinct paths under the given share_factor/weight_factor).
  // For each route, find the earliest index it duplicates (or -1 if unique).
  // A duplicate = same encoded geometry, OR distance+duration match within 0.1%.
  const dupOf: number[] = routes.map((r, i) => {
    for (let j = 0; j < i; j++) {
      const a = r, b = routes[j];
      if (a.geometry && a.geometry === b.geometry) return j;
      const ad = a.summary?.distance, bd = b.summary?.distance;
      const at = a.summary?.duration, bt = b.summary?.duration;
      if (ad != null && bd != null && at != null && bt != null) {
        const relD = Math.abs(ad - bd) / Math.max(ad, 1);
        const relT = Math.abs(at - bt) / Math.max(at, 1);
        if (relD < 0.001 && relT < 0.001) return j;
      }
    }
    return -1;
  });
  const dupCount = dupOf.filter(x => x >= 0).length;

  return (
    <div className="routes-panel">
      <div className="routes-panel-meta">
        <span>{routes.length} route{routes.length > 1 ? 's' : ''}</span>
        <span className="muted">· {elapsedMs} ms</span>
        {dupCount > 0 && (
          <span
            className="muted"
            style={{ color: 'var(--warn)' }}
            title="ORS returned duplicate routes. Try lowering share_factor (more diverse) or raising weight_factor (accept worse alternates)."
          >
            · ⚠ {dupCount} duplicate{dupCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="routes-list">
        {routes.map((_r, i) => {
          const st = stats[i];
          const isSelected = i === selectedIdx;
          const visible = visibleRoutes[i] !== false;
          const km = parseFloat(st.km);
          const dur = routes[i].summary?.duration ?? 0;
          const dKm = i === 0 ? 0 : km - primaryKm;
          const dDur = i === 0 ? 0 : dur - primaryDur;
          const sign = (n: number) => (n > 0 ? '+' : '');
          const isDup = dupOf[i] >= 0;
          return (
            <div
              key={i}
              className={`route-card${isSelected ? ' selected' : ''}${!visible ? ' hidden-route' : ''}${isDup ? ' duplicate' : ''}`}
              style={{ borderLeftColor: routeColor(i) }}
              onClick={() => onSelect(i)}
              title={isDup
                ? `Identical to Route ${dupOf[i] + 1}. ORS could not find a distinct alternate — try lowering share_factor or raising weight_factor.`
                : 'Click to highlight this route on the map'}
            >
              <div className="route-card-head">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => onToggleVisible(i)}
                  onClick={(e) => e.stopPropagation()}
                  title="Show/hide this route on the map"
                />
                <span className="route-swatch" style={{ background: routeColor(i) }} />
                <b>Route {i + 1}</b>
                {i === 0 && <span className="route-badge">primary</span>}
                {isSelected && <span className="route-badge sel">selected</span>}
                {isDup && (
                  <span
                    className="route-badge dup"
                    title={`Same geometry as Route ${dupOf[i] + 1}`}
                  >
                    ⚠ duplicate of #{dupOf[i] + 1}
                  </span>
                )}
              </div>
              <div className="route-card-body">
                <span className="stat-pill"><span className="label">Dist</span><span className="value ok">{st.km} km</span>{i > 0 && <span className={`delta ${dKm > 0 ? 'worse' : 'better'}`}>{sign(dKm)}{dKm.toFixed(1)}</span>}</span>
                <span className="stat-pill"><span className="label">Time</span><span className="value ok">{st.dur}</span>{i > 0 && dDur !== 0 && <span className={`delta ${dDur > 0 ? 'worse' : 'better'}`}>{sign(dDur)}{formatDuration(Math.abs(dDur))}</span>}</span>
                {st.avgSpeed != null && <span className="stat-pill"><span className="label">Avg</span><span className="value">{st.avgSpeed} km/h</span></span>}
                {st.detourFactor != null && <span className="stat-pill"><span className="label">Detour</span><span className="value">{st.detourFactor}×</span></span>}
                {(st.turnCount || st.keeps || st.roundabouts || st.uturns) ? (
                  <span
                    className="stat-pill"
                    title="Turns / Keep-L-R / Roundabouts / U-turns. Highway 'continue' (type 6) not counted."
                  >
                    <span className="label">T/K/R/U</span>
                    <span className="value">{st.turnCount}/{st.keeps}/{st.roundabouts}/{st.uturns}</span>
                  </span>
                ) : null}
                {st.tollKm && <span className="stat-pill"><span className="label">Toll</span><span className="value warn">{st.tollKm} km</span></span>}
                {st.ascent != null && <span className="stat-pill"><span className="label">Asc</span><span className="value">{Math.round(st.ascent)} m</span></span>}
                {st.descent != null && <span className="stat-pill"><span className="label">Desc</span><span className="value">{Math.round(st.descent)} m</span></span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

